import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Home, ArrowLeft } from "lucide-react";
import { ModernLogo } from "@/components/ModernLogo";
import { SEO } from "@/components/SEO";

export default function TradesmanRegistrationSuccess() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50/30 via-background to-blue-100/20 flex items-center justify-center p-4 relative overflow-hidden">
      <SEO 
        title="Registration Complete - Welcome to Sortable"
        description="Your tradesman registration is complete. Start receiving job opportunities today."
        keywords="tradesman registration complete, join Sortable, tradesman jobs, trade professional platform"
        canonical="/tradesman-registration/success"
      />
      
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-subtle opacity-50"></div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-blue opacity-20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-hero opacity-10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      
      <div className="w-full max-w-2xl relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <button onClick={() => navigate('/')} className="transition-transform hover:scale-105">
              <ModernLogo size="lg" showTagline={false} />
            </button>
          </div>
        </div>

        <Card className="shadow-elegant hover:shadow-glow transition-all duration-300 border-0 bg-gradient-hero text-white animate-fade-in">
          <CardHeader className="bg-white/10 backdrop-blur-sm text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
            </div>
            <CardTitle className="text-3xl text-white">
              Registration Complete!
            </CardTitle>
            <CardDescription className="text-white/90 text-lg">
              Welcome to Sortable - Your tradesman journey starts now
            </CardDescription>
          </CardHeader>
          
          <CardContent className="bg-white/95 backdrop-blur-md p-8 text-center space-y-6">
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">What happens next?</h2>
              
              <div className="grid gap-4 text-left">
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                  <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                  <div>
                    <h3 className="font-semibold text-foreground">Account Review</h3>
                    <p className="text-sm text-muted-foreground">Our team will review your application and documents within 24-48 hours.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
                  <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                  <div>
                    <h3 className="font-semibold text-foreground">Account Approval</h3>
                    <p className="text-sm text-muted-foreground">Once approved, you'll receive an email confirmation and can start receiving job notifications.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg">
                  <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</div>
                  <div>
                    <h3 className="font-semibold text-foreground">Start Earning</h3>
                    <p className="text-sm text-muted-foreground">Browse available jobs, submit quotes, and start building your customer base.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-hero text-white shadow-glow hover:shadow-elegant transition-all duration-300 border-0 flex items-center gap-2"
                size="lg"
              >
                <Home className="w-5 h-5" />
                Return to Homepage
              </Button>
              
              <Button
                onClick={() => navigate('/for-tradesmen')}
                variant="outline"
                className="border-blue-200 text-blue-600 hover:bg-blue-50 transition-all duration-300 flex items-center gap-2"
                size="lg"
              >
                Learn More About Sortable
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}